import React, { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';

function EventFormAndList() {
  const [formData, setFormData] = useState({
    title: '',
    number: '',
    date: '',
    pointOfContact: '',
    address: '',
    image: ''
  });
  const [loading, setLoading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [imagePreview, setImagePreview] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({...formData, [name]: value});
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({...formData, image: reader.result});
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setUploadStatus('');

    try {
      await axios.post(`${process.env.REACT_APP_REQUESTING_API}/api/events/create`, formData);
      setUploadStatus('✅ Event created successfully!');
      setFormData({
        title: '',
        number: '',
        date: '',
        pointOfContact: '',
        address: '',
        image: ''
      });
      setImagePreview('');
      setTimeout(() => setUploadStatus(''), 5000);
    } catch (err) {
      console.error(err);
      setUploadStatus('❌ Failed to create event. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-black text-gray-800 mb-3">
            Create New Event
          </h1>
          <p className="text-gray-600 text-lg">
            Add an upcoming event to your calendar
          </p>
        </motion.div>

        {/* Upload Status Message */}
        {uploadStatus && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-6 p-4 rounded-xl font-semibold text-center ${
              uploadStatus.includes('✅')
                ? 'bg-green-100 text-green-800 border-2 border-green-300'
                : 'bg-red-100 text-red-800 border-2 border-red-300'
            }`}
          >
            {uploadStatus}
          </motion.div>
        )}

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-200"
          >
            <div className="space-y-6">
              {/* Event Title */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Event Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="e.g., Annual Community Gathering"
                  required
                  className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all"
                />
              </div>

              {/* Event Date */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Event Date *
                </label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                  min={new Date().toISOString().split("T")[0]}
                  className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all"
                />
              </div>

              {/* Two Column Grid */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Point of Contact */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Point of Contact Name *
                  </label>
                  <input
                    type="text"
                    name="pointOfContact"
                    value={formData.pointOfContact}
                    onChange={handleChange}
                    placeholder="Enter contact name"
                    required
                    className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all"
                  />
                </div>

                {/* Contact Number */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Contact Number *
                  </label>
                  <input
                    type="text"
                    name="number"
                    value={formData.number}
                    onChange={handleChange}
                    placeholder="Enter phone number"
                    required
                    className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all"
                  />
                </div>
              </div>

              {/* Address */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Event Address *
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Enter event venue address"
                  required
                  className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all"
                />
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Upload Event Image *
                </label>
                <input
                  type="file"
                  onChange={handleFileChange}
                  accept="image/*"
                  required
                  className="w-full p-4 border-2 border-gray-200 rounded-xl file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-gray-800 file:text-white file:font-semibold hover:file:bg-gray-900 cursor-pointer"
                />
                {imagePreview && (
                  <div className="mt-4 flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-32 h-32 object-cover rounded-lg shadow-md border-2 border-gray-200"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-green-700">✓ Image loaded successfully</p>
                      <p className="text-xs text-gray-500 mt-1">Preview of event image</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gray-800 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-900 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-3">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Creating Event...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Create Event
                  </span>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
}

export default EventFormAndList;


// working 16/12/25

// import React, { useState } from 'react';
// import axios from 'axios';

// function EventFormAndList() {
//   const [formData, setFormData] = useState({
//     title: '',
//     number: '',
//     date: '',
//     pointOfContact: '',
//     address: '',
//     image: ''
//   });

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setFormData({...formData, [name]: value});
//   };

//   const handleFileChange = (e) => {
//     const file = e.target.files[0];
//     if (file) {
//       const reader = new FileReader();
//       reader.onloadend = () => {
//         setFormData({...formData, image: reader.result});
//       };
//       reader.readAsDataURL(file);
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     try {
//       await axios.post(`${process.env.REACT_APP_REQUESTING_API}/api/events/create`, formData);
//       alert('Event Created Successfully!');
//       setFormData({
//         title: '',
//         number: '',
//         date: '',
//         pointOfContact: '',
//         address: '',
//         image: ''
//       });
//     } catch (err) {
//       console.error(err);
//       alert('Error creating event');
//     }
//   };

//   return (
//     <div className="max-w-3xl mx-auto p-6">
//       <h2 className="text-3xl font-bold text-center mb-8">Create New Event</h2>

//       <form onSubmit={handleSubmit} className="bg-white p-8 rounded shadow-md">
        
//         <div className="mb-4">
//           <label className="block mb-1">Event Title</label>
//           <input
//             type="text"
//             name="title"
//             value={formData.title}
//             onChange={handleChange}
//             placeholder="Event Title"
//             required
//             className="w-full p-2 border rounded"
//           />
//         </div>

//         <div className="mb-4">
//           <label className="block mb-1">Event Date</label>
//          <input
//   type="date"
//   name="date"
//   value={formData.date}
//   onChange={handleChange}
//   required
//   min={new Date().toISOString().split("T")[0]}
//   className="w-full p-2 border rounded"
// />

//         </div>

//         <div className="mb-4">
//           <label className="block mb-1">Point of Contact Name</label>
//           <input
//             type="text"
//             name="pointOfContact"
//             value={formData.pointOfContact}
//             onChange={handleChange}
//             placeholder="Point of Contact"
//             required
//             className="w-full p-2 border rounded"
//           />
//         </div>

//         <div className="mb-4">
//           <label className="block mb-1">Point of Contact Number</label>
//           <input
//             type="text"
//             name="number"
//             value={formData.number}
//             onChange={handleChange}
//             placeholder="Contact Number"
//             required
//             className="w-full p-2 border rounded"
//           />
//         </div>

//         <div className="mb-4">
//           <label className="block mb-1">Address</label>
//           <input
//             type="text"
//             name="address"
//             value={formData.address}
//             onChange={handleChange}
//             placeholder="Address"
//             required
//             className="w-full p-2 border rounded"
//           />
//         </div>

//         <div className="mb-4">
//           <label className="block mb-1">Upload Image Folder</label>
//           <input
//             type="file"
//             onChange={handleFileChange}
//             accept="image/*"
//             className="w-full"
//           />
//         </div>

//         <div className="flex justify-center mt-6">
//           <button
//             type="submit"
//             className="px-12 py-2 bg-gray-600 text-white rounded text-md hover:bg-gray-700"
//           >
//             Submit
//           </button>
//         </div>
//       </form>
//     </div>
//   );
// }

// export default EventFormAndList;

